import { useMemo, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatUtcDate } from "@/utils/dateUtils";
import { axisValueLabel, EIXO_LABEL } from "@/utils/disponibilidadeUtils";
import {
  useFreelancerTimelineCompleta,
  type PeriodoTimeline,
  type TimelineEvento,
} from "@/hooks/useFreelancerTimelineCompleta";
import type { TimelineSegment } from "@/hooks/useDisponibilidade";
import { cn } from "@/lib/utils";
import {
  TimelineLane,
  CLASSE_HACHURA_SEM_INFO,
  type LaneSegmento,
} from "@/components/timeline/TimelineLane";
import { EventDetailDialog } from "@/components/timeline/EventDetailDialog";
import { LANE_CONFIG, LANE_ORDER, laneConfig } from "@/components/timeline/timelineEventUtils";
import { PeriodoToggle } from "@/components/timeline/PeriodoToggle";
import { TimelineFeed } from "./TimelineFeed";
import { TimelineResumoCards } from "./TimelineResumoCards";

const MESES_POR_PERIODO: Record<Exclude<PeriodoTimeline, "tudo">, number> = {
  "12m": 12,
  "24m": 24,
};

/** Cor + rótulo do resumo (disponibilidade efetiva) de um segmento de estado. */
function resumoDoSegmento(segment: TimelineSegment): { className: string; rotulo: string } {
  const cargaAtual = segment.carga?.carga ?? 0;
  if (cargaAtual > 0) {
    return {
      className: "bg-blue-500",
      rotulo:
        cargaAtual > 1 ? `${cargaAtual} projetos com a gente` : "Em projeto com a gente",
    };
  }
  const interesse = segment.eixos?.interesse_freela?.value;
  if (interesse === "topa_freela") return { className: "bg-emerald-400", rotulo: "Topa freela" };
  if (interesse === "aberto_mas_ocupado")
    return { className: "bg-amber-400", rotulo: "Aberta, mas ocupada" };
  if (interesse === "nao_topa") return { className: "bg-rose-400", rotulo: "Não topa" };
  const emprego = segment.eixos?.situacao_emprego?.value;
  if (emprego === "empregado_fixo") return { className: "bg-blue-300", rotulo: "Empregada fixo" };
  if (emprego === "freela_ativo")
    return { className: "bg-emerald-300", rotulo: "Freelancer ativo" };
  return { className: CLASSE_HACHURA_SEM_INFO, rotulo: "Sem info" };
}

function formatMesCurto(ms: number) {
  return new Date(ms).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
}

interface FreelancerTimelineProps {
  freelancerId: string;
}

/**
 * Aba "Linha do tempo" do freelancer: resumos (funil/cadastro/contato), lanes
 * de estado + marcos por eixo (§6.1) e feed cronológico. Uma chamada de RPC
 * (get_freelancer_full_timeline) alimenta a tela inteira.
 */
export function FreelancerTimeline({ freelancerId }: FreelancerTimelineProps) {
  const [periodo, setPeriodo] = useState<PeriodoTimeline>("24m");
  const [mostrarMaisEixos, setMostrarMaisEixos] = useState(false);
  const [laneFiltro, setLaneFiltro] = useState<string | null>(null);
  const [eventoSelecionado, setEventoSelecionado] = useState<TimelineEvento | null>(null);
  const [dialogAberto, setDialogAberto] = useState(false);

  const { data, isLoading, isError, refetch } = useFreelancerTimelineCompleta(
    freelancerId,
    periodo,
  );

  const eventos = useMemo(() => data?.eventos ?? [], [data]);
  const disponibilidade = data?.lanes_estado?.disponibilidade ?? null;
  const golden = data?.golden ?? null;

  // Janela do gráfico: períodos fixos contam a partir de hoje; "tudo" abre até
  // o acontecimento mais antigo (evento ou segmento de disponibilidade).
  const janela = useMemo(() => {
    const fimMs = Date.now();
    if (periodo !== "tudo") {
      const inicio = new Date();
      inicio.setMonth(inicio.getMonth() - MESES_POR_PERIODO[periodo]);
      return { fromMs: inicio.getTime(), toMs: fimMs };
    }
    const candidatos: number[] = [];
    for (const evento of eventos) {
      const occurredMs = new Date(evento.occurred_at).getTime();
      if (!Number.isNaN(occurredMs)) candidatos.push(occurredMs);
    }
    if (disponibilidade?.window?.from) {
      candidatos.push(new Date(disponibilidade.window.from).getTime());
    }
    if (!candidatos.length) {
      const fallback = new Date();
      fallback.setMonth(fallback.getMonth() - 24);
      return { fromMs: fallback.getTime(), toMs: fimMs };
    }
    return { fromMs: Math.min(...candidatos), toMs: fimMs };
  }, [periodo, eventos, disponibilidade]);

  const nowPercent = 100; // a janela sempre termina em "hoje"

  // Lane Resumo: reusa os segmentos do estado de disponibilidade.
  const segmentosResumo = useMemo((): LaneSegmento[] => {
    if (!disponibilidade?.segments?.length) return [];
    return disponibilidade.segments.map((segment) => {
      const visual = resumoDoSegmento(segment);
      const periodoTexto = `${formatUtcDate(segment.from)} até ${
        segment.to ? formatUtcDate(segment.to) : "hoje"
      }`;
      return {
        fromMs: new Date(segment.from).getTime(),
        toMs: segment.to ? new Date(segment.to).getTime() : null,
        className: visual.className,
        tooltip: (
          <div className="space-y-0.5 text-xs">
            <div className="font-medium">{visual.rotulo}</div>
            {Object.entries(segment.eixos ?? {}).map(([dimensao, eixo]) => (
              <div key={dimensao} className="text-muted-foreground">
                {EIXO_LABEL[dimensao] ?? dimensao}: {axisValueLabel(dimensao, eixo?.value)}
              </div>
            ))}
            <div className="text-muted-foreground">{periodoTexto}</div>
          </div>
        ),
      };
    });
  }, [disponibilidade]);

  // Eventos agrupados por lane (lanes desconhecidas rendem no fim, com fallback).
  const eventosPorLane = useMemo(() => {
    const porLane = new Map<string, TimelineEvento[]>();
    for (const evento of eventos) {
      const listaDaLane = porLane.get(evento.lane) ?? [];
      listaDaLane.push(evento);
      porLane.set(evento.lane, listaDaLane);
    }
    return porLane;
  }, [eventos]);

  const lanesDesconhecidas = useMemo(
    () =>
      Array.from(eventosPorLane.keys()).filter(
        (lane) => !LANE_ORDER.includes(lane as (typeof LANE_ORDER)[number]),
      ),
    [eventosPorLane],
  );

  const lanesDoFiltro = useMemo(() => {
    const presentes = LANE_ORDER.filter((lane) => eventosPorLane.has(lane));
    return [...presentes, ...lanesDesconhecidas];
  }, [eventosPorLane, lanesDesconhecidas]);

  const eventosDoFeed = useMemo(
    () => (laneFiltro ? eventos.filter((evento) => evento.lane === laneFiltro) : eventos),
    [eventos, laneFiltro],
  );

  const abrirEvento = (evento: TimelineEvento) => {
    setEventoSelecionado(evento);
    setDialogAberto(true);
  };

  const temAlgumDado = eventos.length > 0 || segmentosResumo.length > 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <Skeleton className="h-40" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-2 py-6 text-center">
        <p className="text-sm text-muted-foreground">
          Não foi possível carregar a linha do tempo.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  if (!temAlgumDado) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <PeriodoToggle periodo={periodo} onChange={setPeriodo} />
        </div>
        <p className="py-6 text-center text-sm text-muted-foreground">
          Sem histórico registrado ainda.
        </p>
      </div>
    );
  }

  const renderLaneDeEventos = (lane: string) => {
    const eventosDaLane = eventosPorLane.get(lane);
    if (!eventosDaLane?.length) return null;
    return (
      <TimelineLane
        key={lane}
        label={laneConfig(lane).label}
        laneKey={lane}
        windowFromMs={janela.fromMs}
        windowToMs={janela.toMs}
        nowPercent={nowPercent}
        eventos={eventosDaLane}
        connectBy={lane === "vagas" ? (evento) => evento.vaga_id : undefined}
        onEventoClick={abrirEvento}
      />
    );
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-5">
        {/* Controles: período + eixos extras */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <PeriodoToggle periodo={periodo} onChange={setPeriodo} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMostrarMaisEixos((atual) => !atual)}
          >
            {mostrarMaisEixos ? "Menos eixos" : "Mostrar mais eixos"}
          </Button>
        </div>

        {/* Resumos de vida (funil, cadastro, contato) */}
        <TimelineResumoCards
          funil={golden?.funil ?? null}
          frescor={golden?.frescor ?? null}
          engajamento={golden?.engajamento ?? null}
        />

        {/* Gráfico por lanes */}
        <div className="space-y-2">
          {/* Eixo de tempo alinhado às barras (espaçador do tamanho do rótulo) */}
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="w-[104px] shrink-0" aria-hidden />
            <div className="flex flex-1 justify-between">
              <span>{formatMesCurto(janela.fromMs)}</span>
              <span>hoje</span>
            </div>
          </div>

          {/* 1. Resumo (estado de disponibilidade) */}
          {segmentosResumo.length > 0 && (
            <TimelineLane
              label="Resumo"
              windowFromMs={janela.fromMs}
              windowToMs={janela.toMs}
              nowPercent={nowPercent}
              segments={segmentosResumo}
            />
          )}

          {/* 2. Perfil, 3. Relacionamento (expansíveis via "Mostrar mais eixos"),
              4. Vagas (marcos da mesma vaga ligados), 5. Projetos, 6. Avaliações */}
          {LANE_ORDER.map((lane) =>
            LANE_CONFIG[lane].defaultOpen || mostrarMaisEixos
              ? renderLaneDeEventos(lane)
              : null,
          )}

          {/* Lanes novas do registry que o front ainda não conhece */}
          {lanesDesconhecidas.map((lane) => renderLaneDeEventos(lane))}

          {/* Legenda */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground/80">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-sm border border-slate-200 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,#e2e8f0_2px,#e2e8f0_4px)]"
                aria-hidden
              />
              Sem info no período
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-full border border-dashed border-slate-400 opacity-70"
                aria-hidden
              />
              Reconstruído do histórico
            </span>
          </div>
        </div>

        {/* Feed cronológico com filtro por lane */}
        <div className="space-y-2 border-t pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              Acontecimentos ({eventosDoFeed.length})
            </h3>
            {lanesDoFiltro.length > 1 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <FiltroLaneChip
                  ativo={laneFiltro === null}
                  rotulo="Tudo"
                  onClick={() => setLaneFiltro(null)}
                />
                {lanesDoFiltro.map((lane) => (
                  <FiltroLaneChip
                    key={lane}
                    ativo={laneFiltro === lane}
                    rotulo={laneConfig(lane).label}
                    onClick={() => setLaneFiltro(laneFiltro === lane ? null : lane)}
                  />
                ))}
              </div>
            )}
          </div>
          <TimelineFeed eventos={eventosDoFeed} onEventoClick={abrirEvento} />
        </div>

        {/* Detalhe do evento (irmão das listas, nunca aninhado em Sheet) */}
        <EventDetailDialog
          evento={eventoSelecionado}
          open={dialogAberto}
          onOpenChange={setDialogAberto}
        />
      </div>
    </TooltipProvider>
  );
}

function FiltroLaneChip({
  ativo,
  rotulo,
  onClick,
}: {
  ativo: boolean;
  rotulo: string;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="focus:outline-hidden">
      <Badge
        variant={ativo ? "default" : "outline"}
        className={cn("cursor-pointer select-none", !ativo && "hover:bg-accent")}
      >
        {rotulo}
      </Badge>
    </button>
  );
}

export default FreelancerTimeline;
